import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, FileQuestion, ServerCrash, Home, RefreshCw, LockKeyhole } from 'lucide-react';

// --- Premium UI Container ---
const FallbackContainer = ({ icon: Icon, title, message, primaryAction, secondaryAction }) => {
    return (
        <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center animate-fadeIn">
            {/* Animated Icon Background */}
            <div className="relative mb-8 group">
                <div className="absolute inset-0 bg-[#2563EB] dark:bg-[#2563EB]/20 rounded-full blur-xl transform group-hover:scale-110 transition-transform duration-700 opacity-70"></div>
                <div className="relative bg-white dark:bg-gray-800 p-6 rounded-full shadow-2xl ring-1 ring-gray-100 dark:ring-gray-700 transform group-hover:-translate-y-1 transition-transform duration-300">
                    <Icon className="w-16 h-16 text-[#2563EB] dark:text-[#2563EB]" strokeWidth={1.5} />
                </div>
            </div>

            {/* Content */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                {title}
            </h2>

            <p className="text-gray-500 dark:text-gray-400 max-w-lg mb-10 text-lg leading-relaxed">
                {message}
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                {primaryAction && (
                    <button
                        onClick={primaryAction.onClick}
                        className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-[#2563EB] rounded-xl hover:bg-[#2563EB] focus:ring-4 focus:ring-[#2563EB]/30 transition-all duration-300 shadow-lg hover:shadow-[#2563EB]/25"
                    >
                        {primaryAction.icon && <primaryAction.icon className="w-5 h-5 mr-2.5" />}
                        {primaryAction.text}
                    </button>
                )}

                {secondaryAction && (
                    <button
                        onClick={secondaryAction.onClick}
                        className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300"
                    >
                        {secondaryAction.icon && <secondaryAction.icon className="w-5 h-5 mr-2.5" />}
                        {secondaryAction.text}
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Specific Page exports ---

// 404 - Not Found
export const NotFoundPage = () => {
    const navigate = useNavigate();
    return (
        <FallbackContainer
            icon={FileQuestion}
            title="Page Not Found"
            message="We couldn't find the page you're looking for. It might have been moved, deleted, or never existed."
            primaryAction={{
                text: "Go Home",
                icon: Home,
                onClick: () => navigate('/')
            }}
            secondaryAction={{
                text: "Go Back",
                onClick: () => navigate(-1)
            }}
        />
    );
};

// 500/502 - Server Error (Generic for User)
export const ServerErrorPage = ({ onRetry }) => {
    const handleRetry = () => {
        if (onRetry) onRetry();
        else window.location.reload();
    };

    return (
        <FallbackContainer
            icon={ServerCrash}
            title="Something Went Wrong"
            message="We're having trouble connecting to our servers right now. Please try again in a few moments."
            primaryAction={{
                text: "Try Again",
                icon: RefreshCw,
                onClick: handleRetry
            }}
            secondaryAction={{
                text: "Go Home",
                icon: Home,
                onClick: () => window.location.href = '/'
            }}
        />
    );
};

// 403 - Forbidden / Access Denied
export const AccessDeniedPage = () => {
    return <ProtectedPage isForbidden={true} />;
};

// Protected / Unauthorized Route (Generic)
export const ProtectedPage = ({ isForbidden, onLogin, onBack }) => {
    const navigate = useNavigate();

    // Customize message based on whether it's "Need Login" or "Access Denied" (Forbidden)
    const title = isForbidden ? "Access Restricted" : "Sign In Required";
    const message = isForbidden
        ? "You don't have permission to access this area. If you believe this is an error, please contact support."
        : "Please sign in to access this page. It's quick and free to join our community.";

    const primaryAction = isForbidden
        ? { text: "Go Back", onClick: onBack || (() => navigate(-1)) }
        : { text: "Sign In", icon: LockKeyhole, onClick: onLogin || (() => navigate('/login')) };

    const secondaryAction = {
        text: "Go Home",
        icon: Home,
        onClick: () => navigate('/')
    };

    return (
        <FallbackContainer
            icon={ShieldAlert}
            title={title}
            message={message}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
        />
    );
};

export default {
    NotFoundPage,
    ServerErrorPage,
    ProtectedPage,
    AccessDeniedPage
};
